import { useEffect, useState } from 'react';
import { useRouterState, useSearch } from '@tanstack/react-router';
import { BrandMark } from '@cwos/ui-admin';
import { usePermissions } from '../../permissions.js';
import { AskPhibianPanel } from './AskPhibianPanel.js';
import { screenContextFor } from './screenContext.js';

/**
 * Ask Phibian, everywhere.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE GATE IS ABSENCE, NOT DISABLEMENT.
 *
 * A caller without `intelligence:use` renders NOTHING here — no button, no
 * ghost, no tooltip explaining what they cannot have. That is the house rule
 * for permission-shaped rendering (`features/permissions.ts`), and on a
 * floating control it matters more than usual: a persistent grey button in the
 * corner of every screen is a permanent advertisement for a refusal.
 *
 * The gate is `has()`, the scope-blind question, because that is the question
 * the route asks: `intelligence:use` guards both Intelligence routes as a flat
 * key. And `has()` is FALSE while `/auth/me` is in flight, which is the right
 * default here — the button appears when the answer lands, and never before it
 * on a guess.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * ONE SURFACE, NOT TWO.
 *
 * On `/intelligence` the trigger is absent. The page IS this conversation, at
 * full width, with its own composer; a floating button that opens a second
 * copy of it on top of itself is noise, and two live composers labelled "Your
 * question" on one screen is an accessibility defect on top of that.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE SCREEN IT CARRIES.
 *
 * Derived from the ROUTER — pathname plus an allowlist of search keys — by
 * `screenContextFor`, and derived on every render so a navigation behind the
 * panel cannot leave a stale screen on the wire. Nothing is read from the DOM,
 * and a route this app does not describe yields `undefined`, which sends no
 * `context` at all. Page visibility is never authority: the server
 * re-authorizes every retrieval whatever this says.
 */

/** What the shortcut is called, for the tooltip, the hint chip and the label. */
const SHORTCUT_LABEL = 'Control or Command + I';

/** The window event {@link askPhibian} dispatches and the mounted launcher hears. */
const ASK_EVENT = 'phibian:ask';

/**
 * "Explain <metric> for <period>" — the draft a chart or KPI opens Ask with.
 * `period` is what the screen shows: a preset's label, or for a custom range
 * its dates ("2026-09-01 to 2026-09-30") — never the word "Custom".
 */
export function explainDraft(metric: string, period: string): string {
  return `Explain ${metric} for ${period}`;
}

/**
 * Open Ask Phibian with `question` in the composer — the seam a chart or KPI
 * "Ask why" uses, so there is ONE assistant rather than a second one per
 * chart. A no-op where the launcher is not mounted (no `intelligence:use`, or
 * the Ask Phibian page itself), so a caller cannot open a surface the person
 * has no button for. The person still reads and sends the question.
 */
export function askPhibian(question: string): void {
  window.dispatchEvent(new CustomEvent(ASK_EVENT, { detail: question }));
}

export interface AskPhibianProps {
  /** The clock behind the panel's greeting, injected so a test can pin it. */
  readonly now?: Date | undefined;
}

export function AskPhibian({ now }: AskPhibianProps = {}) {
  const { has } = usePermissions();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const search = useSearch({ strict: false });
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string | undefined>(undefined);

  const mayUse = has('intelligence:use');
  // `/intelligence` and any deeper path under it: the page owns the surface.
  const onOwnPage = pathname === '/intelligence' || pathname.startsWith('/intelligence/');
  const available = mayUse && !onOwnPage;

  /**
   * The shortcut. Bound on `document` in the CAPTURE phase, the same way the
   * global search palette binds ⌘K, so a focused input inside a screen cannot
   * swallow it. Registered ONLY when the control is available — an account
   * that has no button must not have a keystroke either, and the listener is
   * torn down when the person's authority or route changes.
   */
  useEffect(() => {
    if (!available) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key !== 'i' && event.key !== 'I') return;
      event.preventDefault();
      setOpen((wasOpen) => !wasOpen);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [available]);

  /** A chart's {@link askPhibian}: open with the question drafted. Same gate as the keystroke. */
  useEffect(() => {
    if (!available) return;
    const onAsk = (event: Event) => {
      const question: unknown = (event as CustomEvent).detail;
      if (typeof question !== 'string' || question.trim() === '') return;
      setDraft(question.slice(0, 500));
      setOpen(true);
    };
    window.addEventListener(ASK_EVENT, onAsk);
    return () => window.removeEventListener(ASK_EVENT, onAsk);
  }, [available]);

  /** However the panel closed (Close, Escape, the shortcut, a lost gate), the draft goes with it. */
  useEffect(() => {
    if (!open) setDraft(undefined);
  }, [open]);

  /**
   * A panel left open by a route change that removed the person's access to
   * this surface would outlive its own gate. Closing on `available` is the
   * cheap way to make the gate total: one condition decides whether this
   * surface exists at all.
   */
  useEffect(() => {
    if (!available) setOpen(false);
  }, [available]);

  if (!available) return null;

  const screen = screenContextFor(pathname, search);

  return (
    <>
      <button
        type="button"
        className="apg-trigger"
        // The visible text is "Ask Phibian"; the accessible name adds the
        // shortcut, spelled out rather than as ⌘, because a screen reader
        // reads "⌘" as anything from "place of interest sign" to silence.
        aria-label={`Ask Phibian (${SHORTCUT_LABEL})`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={`Ask Phibian · ${SHORTCUT_LABEL}`}
        onClick={() => setOpen(true)}
      >
        <BrandMark size={20} />
        <span className="apg-trigger-text">Ask Phibian</span>
      </button>
      {open ? (
        <AskPhibianPanel
          screen={screen}
          onClose={() => setOpen(false)}
          now={now}
          initialDraft={draft}
        />
      ) : null}
    </>
  );
}
